/**
 * format.ts — display-layer string formatting for DB-derived values.
 *
 * Round 13 (2026-06-25). The DB stores canonical lowercase / snake_case /
 * kebab-case slugs (e.g., `marketing-ops`, `active`, `owner`, `preferred`,
 * `auto_run`). API responses also stay lowercase — those are wire-format
 * identifiers, not display strings. This module is the boundary where raw
 * DB values land in the DOM with proper capitalization applied.
 *
 * Two exports:
 *   - `formatLabel(raw)` — sentence-case formatter ("Marketing ops"). Use
 *     for status pills, role labels, tier labels, category tags, and most
 *     small chip surfaces.
 *   - `formatTitle(raw)` — title-case formatter ("Marketing Ops"). Use for
 *     proper-noun-ish headings.
 *
 * Acronyms + brand names are preserved via OVERRIDES (e.g., `oauth` →
 * `OAuth`, `openai` → `OpenAI`, `aws_bedrock` → `AWS Bedrock`).
 *
 * Test cases (spot-check; no runtime tests):
 *   'active'         → 'Active'
 *   'owner'          → 'Owner'
 *   'marketing-ops'  → 'Marketing ops'      (sentence-case)
 *   'permission-set' → 'Permission set'
 *   'oauth'          → 'OAuth'              (acronym)
 *   'aws_bedrock'    → 'AWS Bedrock'        (acronym + 2 words)
 *   'openai'         → 'OpenAI'             (brand)
 *   'gpt-5'          → 'GPT 5'              (acronym + version)
 *   'ga4'            → 'GA4'
 *   'confirm-writes' → 'Confirm writes'
 *   'auto-run'       → 'Auto run'
 *   'sla'            → 'SLA'
 *   ''               → ''
 *   null / undefined → ''
 */

/**
 * Acronym + brand override map. Keys are lowercase tokens; values are the
 * canonical display form. Lookup happens AFTER lowercasing each split token
 * so input casing doesn't matter (DB slugs are lowercase anyway, but
 * defensive against the occasional ALL_CAPS enum that leaks through).
 *
 * Order doesn't matter — the lookup is O(1).
 */
const OVERRIDES: Record<string, string> = {
  // Acronyms — preserve all-caps
  api: 'API',
  ai: 'AI',
  mcp: 'MCP',
  oauth: 'OAuth',
  rls: 'RLS',
  sla: 'SLA',
  crm: 'CRM',
  saas: 'SaaS',
  spa: 'SPA',
  sso: 'SSO',
  sse: 'SSE',
  url: 'URL',
  uri: 'URI',
  uuid: 'UUID',
  jwt: 'JWT',
  http: 'HTTP',
  https: 'HTTPS',
  pdf: 'PDF',
  csv: 'CSV',
  json: 'JSON',
  xml: 'XML',
  html: 'HTML',
  css: 'CSS',
  js: 'JS',
  ts: 'TS',
  aws: 'AWS',
  gcp: 'GCP',
  dns: 'DNS',
  cdn: 'CDN',
  vpc: 'VPC',
  iam: 'IAM',
  ga4: 'GA4',
  gpt: 'GPT',
  llm: 'LLM',
  pkce: 'PKCE',
  byo: 'BYO',
  ps: 'PS',

  // Brand-cased
  openai: 'OpenAI',
  chatgpt: 'ChatGPT',
  hubspot: 'HubSpot',
  linkedin: 'LinkedIn',
  github: 'GitHub',
  gitlab: 'GitLab',
  mailchimp: 'MailChimp',
  salesforce: 'Salesforce',
  postgresql: 'PostgreSQL',
  mysql: 'MySQL',
  mongodb: 'MongoDB',
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  nodejs: 'Node.js',
};

function applyTokenOverride(lower: string, capitalizeIfMissing: boolean): string {
  const override = OVERRIDES[lower];
  if (override !== undefined) return override;
  if (lower === '') return '';
  if (capitalizeIfMissing) return lower.charAt(0).toUpperCase() + lower.slice(1);
  return lower;
}

/**
 * Sentence-case formatter — "marketing-ops" → "Marketing ops",
 * "active" → "Active", "owner" → "Owner". Splits on `-` / `_` / whitespace,
 * applies the override map per token, capitalizes the first token only,
 * joins with spaces.
 *
 * Null / undefined / empty inputs return ''.
 */
export function formatLabel(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '';
  const tokens = raw.split(/[-_\s]+/).filter((t) => t.length > 0);
  if (tokens.length === 0) return '';
  return tokens
    .map((t, i) => {
      const lower = t.toLowerCase();
      // First token: capitalize when no override matches.
      // Subsequent tokens: lowercase when no override matches (sentence-case).
      return applyTokenOverride(lower, i === 0);
    })
    .join(' ');
}

/**
 * Title-case formatter — "marketing-ops" → "Marketing Ops",
 * "permission-set" → "Permission Set". Same splitting + override behavior
 * as `formatLabel`, but every token is capitalized (unless it has an
 * override that dictates otherwise — "openai" still becomes "OpenAI").
 *
 * Use for proper-noun-ish headings where every word should lead. Default
 * to `formatLabel` for chip / pill / status surfaces.
 */
export function formatTitle(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '';
  const tokens = raw.split(/[-_\s]+/).filter((t) => t.length > 0);
  if (tokens.length === 0) return '';
  return tokens
    .map((t) => {
      const lower = t.toLowerCase();
      return applyTokenOverride(lower, true);
    })
    .join(' ');
}
